import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EventPost from '../EventPost/EventPost';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { Button, Typography, Modal, TextField, Box, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { useAuth } from '../AuthContext';

function HomePage() {
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    eventName: '',
    eventDescription: '',
    eventDateTime: '',
    eventLocation: '',
    isPaid: false,
    requiredFields: [],
    isSponsored: false,
    hasRegulations: false,
    regulationsText: ''
  });


  const participantFields = [
    { name: 'eventParticipantName', label: 'Imię' },
    { name: 'eventParticipantSurname', label: 'Nazwisko' },
    { name: 'eventParticipantGender', label: 'Płeć' },
    { name: 'eventParticipantEmail', label: 'Email' },
    { name: 'eventParticipantPhone', label: 'Telefon' },
    { name: 'eventParticipantLocality', label: 'Miejscowość' },
    { name: 'eventParticipantTeam', label: 'Klub (drużyna)' },
    { name: 'eventParticipantShirtSize', label: 'Rozmiar koszulki' },
    { name: 'eventParticipantBirthDate', label: 'Data urodzenia' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEvent(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (fieldName) => {
    setNewEvent(prev => {
      const currentFields = prev.requiredFields || [];
      const updatedFields = currentFields.includes(fieldName)
        ? currentFields.filter(f => f !== fieldName)
        : [...currentFields, fieldName];
      return { ...prev, requiredFields: updatedFields };
    });
  };

  const fetchEvents = async () => {
    const querySnapshot = await getDocs(collection(db, "events"));
    const events = querySnapshot.docs.map(doc => {
      const data = doc.data();
      const eventDateTime = new Date(data.eventDateTime);
      return {
        id: doc.id,
        title: data.eventName,
        description: data.eventDescription,
        date: eventDateTime,
        imageUrl: data.eventImageUrl
      };
    });
    setEvents(events);
  };

  const handleAddEvent = async () => {
    const { eventName, eventDescription, eventDateTime, eventLocation, eventImageUrl } = newEvent;
    if (!eventName || !eventDescription || !eventDateTime || !eventLocation) {
      alert('Wypełnij wszystkie pola!');
      return;
    }
    if (newEvent.hasRegulations && !newEvent.regulationsText.trim()) {
      alert('Wklej treść regulaminu lub odznacz checkbox regulaminu.');
      return;
    }
    try {
      const requiredFieldsObject = {};
      (newEvent.requiredFields || []).forEach(field => {
        requiredFieldsObject[field] = true;
      });
      await addDoc(collection(db, "events"), {
        ...newEvent,
        requiredFields: requiredFieldsObject,
        regulationsText: newEvent.hasRegulations ? newEvent.regulationsText : '',
        eventDateTime: new Date(eventDateTime).toISOString(),
        eventParticipants: []
      });
      setOpen(false);
      alert('Wydarzenie dodane pomyślnie.');
      fetchEvents();
    } catch (error) {
      console.error('Błąd przy dodawaniu wydarzenia:', error);
      alert('Nie udało się dodać wydarzenia.');
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  return (
    <div>
      <Box sx={{ backgroundColor: 'primary.main', py: 2 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            textAlign: 'center',
            color: 'white',
            fontWeight: 'bold',
          }}
        >
          WYDARZENIA
        </Typography>
      </Box>
      {userRole === 'admin' && (
        <Button
          onClick={() => setOpen(true)}
          sx={{ display: 'block', margin: 'auto', backgroundColor: '#004d99', color: 'white', '&:hover': { backgroundColor: '#003366' } }}>
          Dodaj wydarzenie
        </Button>
      )}
      <Box
        sx={{
          maxHeight: '600px',
          overflowY: 'auto',
          marginTop: '20px',
          padding: '10px',
          border: '1px solid rgba(0, 0, 0, 0.2)',
          borderRadius: '8px'
        }}
      >
        {events.map(event => (
          <EventPost key={event.id} {...event} onClick={() => navigate(`/event/${event.id}`)} />
        ))}
      </Box>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90vw', sm: 500 },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            p: 3,
            borderRadius: 2,
            boxShadow: 24,
            overflowY: 'auto'
          }}
        >
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Dodaj nowe wydarzenie
          </Typography>

          <TextField fullWidth sx={{ mt: 2 }} name="eventName" label="Nazwa wydarzenia" variant="outlined" onChange={handleInputChange} />
          <TextField fullWidth multiline rows={4} sx={{ mt: 2 }} name="eventDescription" label="Opis wydarzenia" variant="outlined" onChange={handleInputChange} />
          <TextField fullWidth sx={{ mt: 2 }} name="eventDateTime" label="Data i czas wydarzenia" type="datetime-local" InputLabelProps={{ shrink: true }} onChange={handleInputChange} />
          <TextField fullWidth sx={{ mt: 2 }} name="eventLocation" label="Miejsce startu" variant="outlined" onChange={handleInputChange} />
          <FormControlLabel
            control={
              <Checkbox
                checked={newEvent.isPaid}
                onChange={(e) => setNewEvent(prev => ({ ...prev, isPaid: e.target.checked }))}
              />
            }
            label="Wydarzenie jest płatne"
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newEvent.isSponsored}
                onChange={(e) => setNewEvent(prev => ({ ...prev, isSponsored: e.target.checked }))}
              />
            }
            label="Wydarzenie sponsorowane"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newEvent.hasRegulations}
                onChange={(e) => setNewEvent(prev => ({ ...prev, hasRegulations: e.target.checked }))}
              />
            }
            label="Wydarzenie posiada regulamin"
          />
          {newEvent?.hasRegulations && (
            <TextField
              fullWidth
              multiline
              rows={4}
              sx={{ mt: 2 }}
              name="regulationsText"
              label="Treść regulaminu"
              variant="outlined"
              value={newEvent.regulationsText || ''}
              onChange={handleInputChange}
            />
          )}
          <Typography variant="subtitle1" sx={{ mt: 2 }}>Wymagane pola rejestracji uczestników:</Typography>
          <FormGroup>
            {participantFields.map(field => (
              <FormControlLabel
                key={field.name}
                control={
                  <Checkbox
                    checked={newEvent.requiredFields.includes(field.name)}
                    onChange={() => handleCheckboxChange(field.name)}
                  />
                }
                label={field.label}
              />
            ))}
          </FormGroup>

          <Button onClick={handleAddEvent} sx={{ mt: 2 }} variant="contained" color="primary">
            Dodaj
          </Button>
        </Box>
      </Modal>
    </div>
  );
}

export default HomePage;
